/**
 * The web's side of the salary PIN (ADR 0041): one place that knows whether a PIN prompt is open and
 * when this session's unlock runs out.
 *
 * The API decides what is locked. When it refuses a pay-bearing response with 403 SALARY_PIN_REQUIRED,
 * `@hrms/api-client` calls `requestSalaryUnlock` and waits: true sends the request once more, false
 * hands the 403 to the screen. SINGLE-FLIGHT — a payroll page fires a handful of gated queries at once,
 * and every one of them must wait on the same dialog, not open its own.
 *
 * Framework-free (a `useSyncExternalStore` store) and deliberately free of `lib/api.ts` and
 * `lib/query-client.ts`: `api.ts` imports this to wire the hook, so importing back would be a cycle.
 * What happens to cached figures on relock is the host's business (`SalaryPinHost`), told through
 * `onRelock`.
 */

import type { SalaryPinRequiredDetails } from '@hrms/shared';

export interface SalaryPinGateSnapshot {
  /** Set while a prompt is open: why the API refused. */
  readonly pending: SalaryPinRequiredDetails | null;
  /** ISO instant this session's unlock ends; null when locked (as far as this tab knows). */
  readonly unlockedUntil: string | null;
}

/**
 * Two gated requests sent together can come back on either side of the dialog closing. One refused just
 * after the unlock was the same refusal, so it is retried without a second prompt. Only briefly after an
 * unlock: past that, a refusal means the server relocked (signed out elsewhere, Redis restarted) and the
 * person must be asked again.
 */
const JUST_UNLOCKED_GRACE_MS = 3_000;

type Listener = () => void;

let snapshot: SalaryPinGateSnapshot = { pending: null, unlockedUntil: null };
let waiters: Array<(unlocked: boolean) => void> = [];
let pendingPromise: Promise<boolean> | null = null;
let unlockedAt = 0;
let relockTimer: ReturnType<typeof setTimeout> | null = null;
const storeListeners = new Set<Listener>();
const relockListeners = new Set<Listener>();
const unlockListeners = new Set<Listener>();

function set(next: SalaryPinGateSnapshot): void {
  snapshot = next;
  storeListeners.forEach((listener) => listener());
}

function stillUnlocked(now = Date.now()): boolean {
  return snapshot.unlockedUntil !== null && Date.parse(snapshot.unlockedUntil) > now;
}

/** The hook `@hrms/api-client` calls. Resolves true once unlocked, false if the person closes the prompt. */
export function requestSalaryUnlock(details: SalaryPinRequiredDetails): Promise<boolean> {
  if (pendingPromise) return pendingPromise;
  if (
    details.reason === 'required' &&
    stillUnlocked() &&
    Date.now() - unlockedAt < JUST_UNLOCKED_GRACE_MS
  ) {
    return Promise.resolve(true);
  }
  pendingPromise = new Promise<boolean>((resolve) => {
    waiters.push(resolve);
  });
  set({ ...snapshot, pending: details });
  return pendingPromise;
}

function settle(unlocked: boolean): void {
  const resolving = waiters;
  waiters = [];
  pendingPromise = null;
  resolving.forEach((resolve) => resolve(unlocked));
}

function relock(): void {
  if (relockTimer) clearTimeout(relockTimer);
  relockTimer = null;
  if (snapshot.unlockedUntil === null) return;
  set({ ...snapshot, unlockedUntil: null });
  relockListeners.forEach((listener) => listener());
}

/**
 * When an unlock with `unlockedForMs` left ends, on THIS device's clock. The API's `unlockedUntil` is on the server's
 * clock; a phone set a few minutes fast would relock early and, with the gated queries refetching, keep relocking.
 */
export function localUnlockEnd(unlockedForMs: number): string {
  return new Date(Date.now() + unlockedForMs).toISOString();
}

/** A PIN was accepted (set, verify, change or reset). Every waiting request is sent again. */
export function resolveUnlock(unlockedUntil: string): void {
  if (relockTimer) clearTimeout(relockTimer);
  unlockedAt = Date.now();
  const delay = Math.max(0, Date.parse(unlockedUntil) - unlockedAt);
  relockTimer = setTimeout(relock, delay);
  set({ pending: null, unlockedUntil });
  settle(true);
  unlockListeners.forEach((listener) => listener());
}

/**
 * Gated data arrived while this tab had no relock timer: the page was reloaded inside the five minutes, or the
 * PIN was entered in another tab (same login session, so the API serves pay). The host asks the API when this
 * session's unlock ends and arms the relock from that. Unlike `resolveUnlock` it settles no waiting request and
 * fires no unlock listeners. `null`, or a time already past, means the server has relocked: drop the figures now.
 */
export function armRelock(unlockedUntil: string | null): void {
  if (relockTimer) clearTimeout(relockTimer);
  relockTimer = null;
  const delay = unlockedUntil === null ? 0 : Date.parse(unlockedUntil) - Date.now();
  if (!(delay > 0)) {
    set({ ...snapshot, unlockedUntil: null });
    relockListeners.forEach((listener) => listener());
    return;
  }
  relockTimer = setTimeout(relock, delay);
  set({ ...snapshot, unlockedUntil });
}

/** True while a relock timer is armed for this tab's unlock. */
export function relockArmed(): boolean {
  return relockTimer !== null;
}

/** The person closed the prompt. Every waiting request gets its 403. */
export function cancelUnlock(): void {
  if (!snapshot.pending && !pendingPromise) return;
  set({ ...snapshot, pending: null });
  settle(false);
}

/**
 * Relock now if the unlock has run out. Timers do not run while the phone app is in the background, so
 * the host calls this when the app comes back to the front.
 */
export function checkSalaryRelock(): void {
  if (snapshot.unlockedUntil !== null && !stillUnlocked()) relock();
}

/** The session ended (sign-out, company switch): close any prompt and forget the unlock. */
export function resetSalaryPinGate(): void {
  cancelUnlock();
  relock();
  unlockedAt = 0;
}

/** Called when the unlock runs out, so cached figures can be dropped. Returns an unsubscribe. */
export function onRelock(listener: Listener): () => void {
  relockListeners.add(listener);
  return () => relockListeners.delete(listener);
}

/** Called after every accepted PIN, so a screen refused earlier can load again. Returns an unsubscribe. */
export function onUnlock(listener: Listener): () => void {
  unlockListeners.add(listener);
  return () => unlockListeners.delete(listener);
}

export function subscribe(listener: Listener): () => void {
  storeListeners.add(listener);
  return () => storeListeners.delete(listener);
}

export function getSnapshot(): SalaryPinGateSnapshot {
  return snapshot;
}
